import React, { createContext, createRef, forwardRef, useContext, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash"
import { Link, useParams, useNavigate, useLocation, useSearchParams, NavLink } from "react-router-dom";
import { Segment, Card, Label, Ref, Form, Icon, Input, Dropdown, Button } from "semantic-ui-react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import MarkerClusterGroup from 'react-leaflet-markercluster';
import L from 'leaflet';
import { useSessionStorage } from './../hooks/useSessionStorage'

import 'leaflet/dist/leaflet.css';
import 'react-leaflet-markercluster/dist/styles.min.css';

import { filterListings,  getCostCount } from '../utils'
import './Map.css'
import { greenLMarker } from '../resources/mapIcons'
import { getCityCount, getCategoryColor, getCategoryHexColor, getServiceDeliveryCount, titleCaseKey, normalizeCategories } from '../utils'
import siteConfig from '../siteConfig.json'

export const EmbedContext = createContext('')
const useBasePath = () => useContext(EmbedContext)

function MapPage({ listings, metadata, ageGroupFilter, setAgeGroupFilter }) {
  const basePath = useBasePath()
  const [ searchParams, ] = useSearchParams()
  const [ search, setSearch ] = useState()
  const navigate = useNavigate()
  // Saved listings write to session storage and persist. Hidden listings reset on page refresh.
  const [saved, setSaved] = useSessionStorage('saved', [])
  const [hidden, setHidden] = useState([])
  const [showSaved, setShowSaved] = useState(false)
  const [hideFaithBased, setHideFaithBased] = useState(false)

  const handleSave = (id, reset=false) => {
    if (reset) { setSaved([]); return; }
    saved.includes(id) ? setSaved(saved.filter(e => e !== id)) : setSaved([...saved, id])
  }
  
  const handleHide = (id, reset=false) => {
    if (reset) { setHidden([]); return; }
    // Remove from saved if it's saved. UX gets wonky otherwise.
    saved.includes(id) && setSaved(saved.filter(e => e !== id))
    setHidden([...hidden, id])
  }

  // "saved" is an array of saved card guids
  const handleShowSaved = () => {
    // If saved is already showing, clear the url bar
    if (searchParams.get('saved')) {
      setShowSaved(false)
      navigate({ pathname: `${basePath}/`, search: ''})
    } else if (saved.length > 0) {
      // set state so that the UI components can update
      setShowSaved(true)
      const paramsString = saved.join("&saved=")
      navigate({ pathname: `${basePath}/`, search: `?saved=${paramsString}` })
    } 
  }

  const { listingCategoryIcons, listingCategories } = metadata

  const debouncedSearch = debounce((value) => { setSearch(value) }, 300);

  const chatRecsParam = searchParams.get('chat_recs')
  const chatRecGuids = useMemo(() => {
    if (!chatRecsParam) return null
    return chatRecsParam.split(',').map(Number).filter(n => !isNaN(n))
  }, [chatRecsParam])

  let filteredListings = useMemo(() => {
    if (chatRecGuids && chatRecGuids.length > 0) {
      const guidSet = new Set(chatRecGuids)
      return listings.filter(l => guidSet.has(l.guid))
    }
    return filterListings(listings, searchParams, search, hidden, { hideFaithBased, ageGroupFilter })
  }, [listings, searchParams, search, hidden, hideFaithBased, ageGroupFilter, chatRecGuids])
  
  let listingCities = useMemo(() => getCityCount(filteredListings ?? {}), [filteredListings])
  const serviceDeliveryCount = useMemo(() => getServiceDeliveryCount(filteredListings ?? {}) || metadata?.listingServiceDelivery || {}, [filteredListings, metadata?.listingServiceDelivery])
  const costCount = useMemo(() => getCostCount(filteredListings ?? {}), [filteredListings])
  
  const cardRefs = listings.reduce((cardRefs, listing) => ({...cardRefs, [listing.guid]: createRef()}), {})
  const mapRef = React.useRef(null)

  const isEmbed = basePath !== ''

  const sponsors = metadata.sponsors || []
  const [panelExpanded, setPanelExpanded] = useState(false)

  useEffect(() => {
    const app = document.getElementById('app')
    if (app) app.classList.add('map-active')
    return () => { if (app) app.classList.remove('map-active') }
  }, [])

  const content = (<>
    <MapSearch listingCategories={listingCategories} listingCategoryIcons={listingCategoryIcons} debouncedSearch={debouncedSearch} listingCities={listingCities} serviceDeliveryCount={serviceDeliveryCount} costCount={costCount} saved={saved} handleSave={handleSave} handleHide={handleHide} hidden={hidden} showSaved={showSaved} handleShowSaved={handleShowSaved} hideFaithBased={hideFaithBased} setHideFaithBased={setHideFaithBased} ageGroupFilter={ageGroupFilter} setAgeGroupFilter={setAgeGroupFilter} />
    <main id="map-page">
      {chatRecGuids && chatRecGuids.length > 0 && (
        <div className="chat-rec-banner">
          <Icon name="chat" />
          <span>Showing {chatRecGuids.length} chat recommendation{chatRecGuids.length !== 1 ? 's' : ''}</span>
          <Button size="mini" basic inverted onClick={() => navigate(`${basePath}/`)} style={{ marginLeft: 'auto' }}>
            <Icon name="close" /> Show All
          </Button>
        </div>
      )}
      {/* Desktop: cards render normally in the grid. Mobile: wrapped in bottom panel via CSS */}
      <div className={`cards mobile-card-panel ${panelExpanded ? 'expanded' : ''}`}>
        <div className="drag-handle" onClick={() => setPanelExpanded(!panelExpanded)} role="button" aria-label={panelExpanded ? 'Collapse results' : 'Expand results'} tabIndex={0} />
        <div className="panel-header" onClick={() => setPanelExpanded(!panelExpanded)} role="button" tabIndex={0}>
          <span>{filteredListings.length} result{filteredListings.length !== 1 ? 's' : ''}</span>
          <Icon name={panelExpanded ? 'chevron down' : 'chevron up'} />
        </div>
        <MapCards listings={filteredListings} cardRefs={cardRefs} mapRef={mapRef} saved={saved} handleSave={handleSave} handleHide={handleHide} />
      </div>
      <div
        className="map-right-col"
        onClick={() => panelExpanded && setPanelExpanded(false)}
        onTouchStart={() => panelExpanded && setPanelExpanded(false)}
      >
        <MapMap listings={filteredListings} cardRefs={cardRefs} ref={mapRef} />
        {sponsors.length > 0 && <SponsorBar sponsors={sponsors} />}
        <AppStoreLink />
      </div>
    </main>
  </>)

  if (isEmbed) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>{content}</div>
        <div style={{ background: '#1B3A5C', padding: '.35em .75em', textAlign: 'center', flexShrink: 0 }}>
          <a href={window.location.origin} target="_blank" rel="noreferrer" style={{ color: '#fff', fontSize: '.8em', textDecoration: 'none' }}>
            <Icon name="map" size="small" /> {siteConfig.siteName}
          </a>
        </div>
      </div>
    )
  }

  return content
}

const sponsorBarStyle = { textAlign: 'center', padding: '1.5em 1em', marginTop: '1em' }
const sponsorLabelStyle = { color: '#888', fontSize: '.85em', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '.75em' }
const sponsorRowStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '1.5em' }
const sponsorLogoStyle = { maxHeight: '60px', width: 'auto', objectFit: 'contain' }

function SponsorBar({ sponsors }) {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  return (
    <Segment basic vertical className="sponsor-bar-segment" style={sponsorBarStyle}>
      <div style={sponsorLabelStyle}>Made Possible By</div>
      <div style={sponsorRowStyle}>
        {sponsors.map((s, i) => {
          const logoUrl = s.logo_url?.startsWith('/') ? `${baseUrl}${s.logo_url}` : s.logo_url
          const hasLogo = logoUrl && logoUrl.length > 0
          if (hasLogo) {
            return s.website_url
              ? <a key={i} href={s.website_url} target="_blank" rel="noreferrer"><img src={logoUrl} alt={s.name} title={s.name} style={sponsorLogoStyle} /></a>
              : <img key={i} src={logoUrl} alt={s.name} title={s.name} style={sponsorLogoStyle} />
          }
          return <span key={i} style={{ fontWeight: 600, color: '#555' }}>{s.name}</span>
        })}
      </div>
    </Segment>
  )
}

const APP_STORE_URL = 'https://apps.apple.com/us/app/wy-youth-resource-map/id6760038717'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=org.casperyouthhub.resourcemap'

function StoreBadges({ className = '' }) {
  return (
    <div className={`app-download-links ${className}`.trim()}>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="store-badge ios"
        aria-label="Download WY Youth Resource Map on the App Store"
      >
        Download on the App Store
      </a>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="store-badge android"
        aria-label="Get WY Youth Resource Map on Google Play"
      >
        Get it on Google Play
      </a>
    </div>
  )
}

function AppStoreLink() {
  return (
    <Segment basic vertical className="app-download-segment" style={{ textAlign: 'center', padding: '1em 1em 1.5em', marginTop: 0 }}>
      <StoreBadges />
    </Segment>
  )
}

function MapSearch({ listingCategories, listingCategoryIcons, debouncedSearch, listingCities, saved, handleShowSaved, serviceDeliveryCount, costCount, hideFaithBased, setHideFaithBased, ageGroupFilter, setAgeGroupFilter }) {
  const basePath = useBasePath()
  const isEmbed = basePath !== ''
  const navigate = useNavigate()
  const location = useLocation()
  const [ searchParams, setSearchParams ] = useSearchParams()
  const [ age, setAge ] = useState(searchParams.get('age') || ``)
  const [showFilters, setShowFilters] = useState(window.innerWidth > 767)
  const showAgeSelector = !isEmbed || searchParams.get('age_select') === '1'

  // When user clears the params, this clears the input value as well
  useEffect(() => {
    setAge(searchParams.get('age') ?? '')
  },[searchParams])

  const MainIconMenu = ()  => 
    <div className="mobile-category-scroll">
    { Object.entries(listingCategories).map(([parentCategory, subCategories]) => {
      const iconName = listingCategoryIcons[parentCategory]?.icon || 'folder'
      const hexColor = getCategoryHexColor(parentCategory)
      const isActive = searchParams.get('category')?.startsWith(`${parentCategory}:`)
      return (
        <div
          key={parentCategory}
          role="button"
          tabIndex={0}
          onClick={() => {
            clearSaved()
            const scrollY = window.scrollY
            const next = new URLSearchParams(searchParams)
            if (isActive) {
              next.delete('category')
            } else {
              next.set('category', `${parentCategory}:`)
            }
            setSearchParams(next, { preventScrollReset: true })
            requestAnimationFrame(() => window.scrollTo(0, scrollY))
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              const scrollY = window.scrollY
              const next = new URLSearchParams(searchParams)
              if (isActive) {
                next.delete('category')
              } else {
                next.set('category', `${parentCategory}:`)
              }
              setSearchParams(next, { preventScrollReset: true })
              requestAnimationFrame(() => window.scrollTo(0, scrollY))
            }
          }}
          style={{ color: 'white', textAlign: 'center', cursor: 'pointer', minWidth: '72px', padding: '4px 8px', opacity: isActive ? 1 : 0.8 }}
          className="category-icon-link"
        >
          <Icon size="big" name={iconName} style={{ color: isActive ? '#F5C518' : hexColor }} />
          <header>{parentCategory}</header>
        </div>
      )
    }) }
    </div>

  const debouncedAge = debounce((value) => { setSearchParams({ ...Object.fromEntries(searchParams), age: value })}, 500)

  // Set the UI state quickly for better UX, but debounce the actual search logic.
  // Note: the "min" and "max" fields on our number input aren't working. Probably a Semantic bug. Implemented manually here.
  const handleAge = value => { if (value >= 0 && value <100) { clearSaved(); setAge(value); debouncedAge(value); }}

  const handleDropdownClick = (eventType, value, key) => {
    clearSaved()
    if (eventType === 'click') {
      if (value === '') {
        searchParams.delete(key); setSearchParams(searchParams)
      }
      else setSearchParams({ ...Object.fromEntries(searchParams), [key]: value }) 
    }
  } 

  // If a user is viewing "saved" listings, and clicks on a different UI element (search, filters, etc) we need to clear the "saved" params from the URL bar before doign anything else, to prevent bugs
  const clearSaved = () => { searchParams.delete('saved') }

  // This data comes from the API. It's optional, so null check it 
  const locationDropdownOptions = Object.entries(listingCities ?? {}).map(([cityName, count]) => {
    return { key: cityName,  text: `${cityName} (${count})`, value: cityName }
  })

  const serviceDeliveryOptions = Object.entries(serviceDeliveryCount ?? {}).map(([delivery, count]) => ({
    key: delivery, text: `${delivery} (${count})`, value: delivery,
  }))

  const costDropdownOptions = Object.entries(costCount ?? {}).map(([cost, count]) => {
    return { key: cost,  text: `${cost} (${count})`, value: cost }
  })

  const locationDropdown = locationDropdownOptions.length > 0
  const serviceDelivery = serviceDeliveryOptions.length > 0
  const cost = costDropdownOptions.length > 0

  const activeFilterCount = [...searchParams].filter(([k]) => k !== 'age_group' && k !== 'saved').length

return (<>
    <Segment as="nav" id="map-nav" color="blue" basic vertical inverted>
      <MainIconMenu />
      <div className="mobile-top-app-downloads">
        <StoreBadges />
      </div>
      {(() => {
        const activeCat = searchParams.get('category')
        if (!activeCat) return null
        const activeParent = Object.keys(listingCategories).find(p => activeCat.startsWith(`${p}:`))
        if (!activeParent) return null
        const subs = Object.keys(listingCategories[activeParent] || {})
        if (subs.length <= 1) return null
        const hexColor = getCategoryHexColor(activeParent)
        const isParentOnly = activeCat === `${activeParent}:`
        return (
          <div className="sub-category-bar">
            <div
              role="button" tabIndex={0}
              className={`sub-pill ${isParentOnly ? 'active' : ''}`}
              style={isParentOnly ? { background: hexColor, borderColor: hexColor, color: '#fff' } : { borderColor: hexColor, color: '#fff' }}
              onClick={() => { const next = new URLSearchParams(searchParams); next.set('category', `${activeParent}:`); setSearchParams(next, { preventScrollReset: true }) }}
            >All</div>
            {subs.map(sub => {
              const fullCat = `${activeParent}: ${sub}`
              const isSubActive = activeCat === fullCat
              return (
                <div
                  key={sub} role="button" tabIndex={0}
                  className={`sub-pill ${isSubActive ? 'active' : ''}`}
                  style={isSubActive ? { background: hexColor, borderColor: hexColor, color: '#fff' } : { borderColor: hexColor, color: '#fff' }}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams)
                    next.set('category', isSubActive ? `${activeParent}:` : fullCat)
                    setSearchParams(next, { preventScrollReset: true })
                  }}
                >{sub}</div>
              )
            })}
          </div>
        )
      })()}
      <Form size="tiny" className="container">
      {/* Search row — always visible */}
      <div className="search-row" style={{ display: 'flex', gap: '.4em', marginTop: '1.5em', marginBottom: showFilters ? 0 : '.25em', alignItems: 'center' }}>
        <Button basic inverted color='yellow' size='small' className="saved-btn-mobile"
            icon={searchParams.get('saved') ? 'list' : 'star outline'}
            disabled={saved.length === 0 && !searchParams.get('saved')} 
            onClick={() => handleShowSaved(saved)}
            title={searchParams.get('saved') ? 'Show All' : 'Show Saved'}
            style={{ minWidth: '44px', minHeight: '36px', padding: '0 8px', flexShrink: 0 }}
        />
        <Button basic inverted color='yellow' size='small' className="saved-btn-desktop"
            icon={searchParams.get('saved') ? 'list' : 'star outline'}
            content={(searchParams.get('saved')) ? 'Show All' : 'Show Saved'}
            disabled={saved.length === 0 && !searchParams.get('saved')} 
            onClick={() => handleShowSaved(saved)}
            style={{minWidth: '130px', flexShrink: 0 }}
        />
        <Input 
          tabIndex="1" fluid
          style={{ flex: 1, minWidth: 0 }}
          icon='search'
          iconPosition="left"
          placeholder="Search..." 
          onFocus={() => navigate(`${basePath}/?${searchParams.toString()}`)} 
          onChange={(e, {value}) => debouncedSearch(value)} 
        />
        <Button 
          icon={<Icon name={showFilters ? "angle up" : "filter"} />}
          onClick={() => setShowFilters(!showFilters)}
          style={{ minWidth: '44px', minHeight: '36px', padding: '0 10px', flexShrink: 0, position: 'relative' }}
        />
        {!showFilters && activeFilterCount > 0 && (
          <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#F5C518', color: '#1B3A5C', borderRadius: '50%', width: '18px', height: '18px', fontSize: '.7em', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{activeFilterCount}</span>
        )}
      </div>

      {/* Filter drawer — collapsible */}
      <div className={`filter-drawer ${showFilters ? '' : 'collapsed'}`}>
        <div className="filter-grid" style={{ marginBottom: '.25em', marginTop: '.5em' }}>
          <Input type="number" id='age-input' fluid
            placeholder='Age'
            value={age}
            onFocus={() => navigate(`${basePath}/?${searchParams.toString()}`)} 
            onChange={(e, {value}) => handleAge(value)} />
          {locationDropdown && 
            <Dropdown id="keyword-input"
              options={locationDropdownOptions} 
              search selection clearable 
              button 
              placeholder={<div><i className="ui icon map marker alternate" id="keyword-icon"></i><span id="keyword-label">  Location</span></div>}
              selectOnBlur={false}  
              inverted fluid
              value={searchParams.get('city') || ``} 
              onFocus={() => navigate(`${basePath}/?${searchParams.toString()}`)} 
              onChange={(e, {value}) => handleDropdownClick(e.type, value, 'city')}
            />}
          {serviceDelivery &&
            <Dropdown id='service-delivery-input'
              options={serviceDeliveryOptions}
              search selection clearable
              button
              placeholder={<div><i className="ui icon video" id="service-delivery-icon"></i><span id="service-delivery-label">  Service Type</span></div>}
              selectOnBlur={false}
              inverted fluid
              value={searchParams.get('service_delivery') || ``}
              onFocus={() => navigate(`${basePath}/?${searchParams.toString()}`)}
              onChange={(e, { value }) => handleDropdownClick(e.type, value, 'service_delivery')}
            />}
          {cost && 
            <Dropdown id='keyword-input'
              options={costDropdownOptions} 
              search selection clearable 
              button
              placeholder={<div><i className="ui icon dollar sign" id="keyword-icon"></i><span id="keyword-label">  Cost</span></div>}
              selectOnBlur={false}
              inverted fluid 
              value={searchParams.get('cost') || ``} 
              onFocus={() => navigate(`${basePath}/?${searchParams.toString()}`)} 
              onChange={(e, {value}) => handleDropdownClick(e.type, value, 'cost')}
            />}
          {showAgeSelector &&
            <Dropdown id='age-group-input'
              options={[
                { key: 'all', text: 'All Ages', value: 'all' },
                { key: 'Youth', text: 'Youth Only', value: 'Youth' },
                { key: 'Adult', text: 'Adult Only', value: 'Adult' },
              ]}
              selection
              button
              fluid
              value={ageGroupFilter}
              onChange={(e, {value}) => setAgeGroupFilter(value)}
              placeholder="Age Group"
            />}
          <div className="full-width" style={{display: 'flex', alignItems: 'flex-start', gap: '3px', padding: '.5em 1em', marginTop: '4px'}}>
            <Form.Checkbox
              toggle
              fitted
              checked={!hideFaithBased}
              onChange={() => setHideFaithBased(!hideFaithBased)}
            />
            <span style={{color: hideFaithBased ? 'white' : '#F5C518', fontSize: '.9em', fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'pointer'}} onClick={() => setHideFaithBased(!hideFaithBased)}>Include faith-based organizations</span>
          </div>
        </div>
      </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '.25em'}}>
          <Label.Group columns={[...searchParams].length} className="doubling container">
            {!location.search.includes('saved') &&
            [...searchParams].filter(([key]) => key !== 'age_group').map(([key, value]) => {
              if (!value) return null
              let displayValue = value
              if (key === 'category') {
                const cleaned = value.endsWith(':') ? value.slice(0, -1) : value
                displayValue = normalizeCategories(cleaned).join(', ')
              }
              return <Label key={key} basic color="blue"><strong>{titleCaseKey(key.replace(/_/ig,` `))}:</strong> {displayValue} <Icon name="delete" onClick={() => { searchParams.delete(key); setSearchParams(searchParams) }} /></Label>
            }) }
          </Label.Group>
        </div>
      </Form>
    </Segment>
  </>)
}

const showButtonStyle = {
  width: '95%',
  marginLeft: '2.5%',
  marginTop: '15px',
  marginBottom: '25px',
}

function MapCards({ listings, cardRefs, mapRef, saved, handleSave, handleHide }) {
  const location = useLocation()
  const { markerId } = useParams()
  const [numCardsShowing, setNumCardsShowing] = useState(25)
  const currentCard = cardRefs[markerId]

  useEffect(() => {
    if (location.state?.scrollToMap) mapRef.current?.scrollIntoView({ behavior: 'smooth' })
    else if (currentCard) currentCard.current?.scrollIntoView({behavior: "smooth"})
  // Only scroll when a specific card/marker is selected, not on filter changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCard, markerId])

  // Limit the number of results shown after search, for speed optimization. User can click "Show More" button to reveal the additional hidden results (all results.)
  const CardsDisplay = ({numEntries}) => {
    if (markerId) {
      const cardsToShow = listings.slice(0, numEntries)
      // Note: Using == for type coercion between string and number
      // eslint-disable-next-line eqeqeq
      if (cardsToShow.find(listing => listing.guid == markerId)) { return cardsToShow.map((listing, index) => <MapCard key={listing.guid} listing={listing} index={index} ref={cardRefs[listing.guid]} mapRef={mapRef} saved={saved?.includes(listing.guid)} handleSave={handleSave} handleHide={handleHide} />) } 
      else {
        // eslint-disable-next-line eqeqeq
        const currentListing = listings.find(listing => listing.guid == markerId) 
        return cardsToShow.concat(currentListing).map((listing, index) => <MapCard key={listing.guid} listing={listing} index={index} ref={cardRefs[listing.guid]} mapRef={mapRef} saved={saved?.includes(listing.guid)} handleSave={handleSave} handleHide={handleHide} />) 
      } 
    }
    return listings.slice(0, numEntries).map((listing, index) => <MapCard key={listing.guid} listing={listing} index={index} ref={cardRefs[listing.guid]} mapRef={mapRef} saved={saved?.includes(listing.guid)} handleSave={handleSave} handleHide={handleHide} />)
  }
  
  return (
    <Card.Group as="section" itemsPerRow="1">
      {/* No results found  */}
      {listings?.length === 0 && <div style={{textAlign: 'center'}}>No Results Found.</div>}
      {/* Show first 30 listings by default if filteredListings is longer than that  */}
      <CardsDisplay numEntries={numCardsShowing} />
      {/* "Show More Listings" button  */}
      {(listings.length > numCardsShowing) && <Button fluid basic color='grey' icon='angle double down' content={`Show more results`} onClick={() => setNumCardsShowing(numCardsShowing + 25)} style={showButtonStyle} />}
    </Card.Group>
  )
}

const CardCornerDropdown = ({ cardColor, guid, handleHide }) => {
  return (
    <Dropdown icon={<Icon name='ellipsis horizontal' color='grey' />} direction='left'>
      <Dropdown.Menu>
        <Dropdown.Item text='Copy link'icon='share alternate' id={`share=${guid}`}
        onClick={() => navigator.clipboard.writeText(`${window.location.origin}/#/${guid}`)}
        />
        <Dropdown.Item as="a" href='/#/suggest' target='_blank' text='Comment' icon={{ name: 'chat', color: cardColor}} />
        <Dropdown.Item onClick={() => handleHide(guid)}
          text='Hide listing' icon={{ name: 'eye slash outline', color: cardColor}} />
      </Dropdown.Menu>
    </Dropdown>
  )
}

const starStyle = { marginRight: '.65em' }
const labelDivStyle = { display: 'flex', justifyContent: 'space-between' }
const cardStyle = { maxWidth: '525px' }
const tagStyle = { color: 'dimgrey' }
const detailsStyle = { marginTop: '.10em', padding: '.75em' }
const blueCheckStyle = { color: 'grey', fontStyle: 'italic' }
const socialLinkStyle = { display: 'flex' }

const MapCard = forwardRef(({ mapRef, listing, saved, handleSave, handleHide, index}, ref) => {
  const { guid, category, parent_organization, full_name, full_address, description, text_message_instructions, phone_1, phone_label_1, phone_1_ext, phone_2, phone_label_2, crisis_line_number, crisis_line_label, website, twitter_link, facebook_link, youtube_link, instagram_link, program_email, languages_offered, keywords, min_age, max_age, eligibility_requirements, covid_message, financial_information, intake_instructions, agency_verified, date_agency_verified, cost_keywords, image_url, photo_urls, office_entrance_image_url, internal_directions } = listing
  const listingCategories = normalizeCategories(category)
  const primaryCategory = listingCategories[0] || ''

  const rawPhotos = (photo_urls && photo_urls.length > 0) ? photo_urls : (image_url ? [image_url] : [])
  const photos = rawPhotos.filter(url => url && !url.includes('maps.googleapis.com'))
  const [photoIndex, setPhotoIndex] = useState(0)

  const basePath = useBasePath()
  const navigate = useNavigate()
  const [ searchParams, setSearchParams ] = useSearchParams()
  const cardColor = getCategoryColor(category)
  const updateSearchParams = (key, val) => {
    const currentParams = Object.fromEntries([...searchParams])
    const newParams = { ...currentParams, key: val }
    setSearchParams(newParams)
  }

  const BlueCheck = ({name, date}) => (
    <div style={blueCheckStyle}><Icon name='check' color={cardColor} /> Verified by {name} on {date}</div>
  )

  const SocialMediaDisplay = ({link, icon}) => (
    <Card.Description><a target="_blank" rel="noreferrer" href={link}>
      <Icon name={icon} /></a>
    </Card.Description> 
  )

  const KeywordDisplay = ({arr}) => arr.map((keyword, i) => (
    <NavLink to={`${basePath}/?${new URLSearchParams({...Object.fromEntries(searchParams), tag: `${keyword}` }).toString()}`} key={keyword} onClick={() => updateSearchParams('tag', keyword)} style={tagStyle}> # {keyword}</NavLink> )
  ) 

  return (
    <Ref innerRef={ref}>
      <Card as="article" color={cardColor} centered raised className="map-card" style={cardStyle}>
        <Card.Content>
        {/* Extra divs are necessary for flex box spacing  */}
          <div style={labelDivStyle}>
            <Label as={Link} to={parent_organization ? `${basePath}/?parent_organization=${encodeURIComponent(parent_organization)}` : `${basePath}/?full_name=${encodeURIComponent(full_name)}`} ribbon color={cardColor} style={{marginBottom: `1em`}}>{parent_organization || full_name}</Label>
            <div> 
              <Icon name={saved ? 'star' : 'star outline'} color={cardColor} style={starStyle} onClick={() => handleSave(guid)} />
              {CardCornerDropdown({cardColor, guid, full_address, mapRef, handleHide})}
            </div>
          </div>
          {/* Header  */}
          <Card.Header><Link to={`${basePath}/${guid}`}>{full_name}</Link></Card.Header>
          { full_address && <Card.Meta style={{ cursor: 'pointer' }} onClick={() => { navigate(`${basePath}/${guid}`, { state: { scrollToMap: true } }) }} title="View on map"><Icon name="map marker alternate" /> {full_address}</Card.Meta> }
          {/* Address & Contact Info  */}
          <Segment secondary>
            { full_address && <Card.Description><Icon name="map marker alternate" /><a target="_blank" rel="noreferrer" href={`https://www.google.com/maps/dir//${encodeURIComponent(full_address)}`}>Get Directions <sup><Icon size="small" name="external" /></sup></a></Card.Description> }
            { phone_1 && <Card.Description><Icon name="phone" />{ phone_label_1 && `${phone_label_1}: ` }<a target="_blank" rel="noreferrer" href={`tel:${phone_1}`}>{phone_1}</a> { phone_1_ext && phone_1_ext}</Card.Description> }
            { phone_2 && <Card.Description><Icon name="phone" />{ phone_label_2 && `${phone_label_2}: ` }<a target="_blank" rel="noreferrer" href={`tel:${phone_2}`}>{phone_2}</a> { phone_1_ext && phone_1_ext}</Card.Description> }
            { crisis_line_number && <Card.Description><Icon name="phone" />{crisis_line_label ?? 'Crisis Line'}: <a target="_blank" rel="noreferrer" href={`tel:${crisis_line_number}`}>{crisis_line_number}</a></Card.Description> }
            {/* Text message is a string and should not be hyperlinked */}
            { text_message_instructions && <Card.Description><Icon name="comment alternate" /> {text_message_instructions}</Card.Description> }
            { program_email && <Card.Description><Icon name="mail outline" /><a target="_blank" rel="noreferrer" href={`mailto:${program_email}`}>{program_email}</a></Card.Description> }
            <div id='social-links' style={socialLinkStyle}>
              {website && <Card.Description style={{marginRight: '3%'}}><Icon name="globe" /><a target="_blank" rel="noreferrer" href={website}>Website</a>  </Card.Description> }
              {instagram_link && <SocialMediaDisplay icon='instagram' link={instagram_link} /> }
              {facebook_link && <SocialMediaDisplay icon='facebook' link={facebook_link} /> }
              {twitter_link && <SocialMediaDisplay icon='twitter' link={twitter_link} /> }
              {youtube_link && <SocialMediaDisplay icon='youtube' link={youtube_link} /> }
            </div>
          </Segment>
          {(agency_verified && date_agency_verified) && <BlueCheck name={full_name} date={date_agency_verified} />}

          {!listing.keywords?.includes('Online Only') && photos.length > 0 && (
            <div style={{margin: '.75em 0', textAlign: 'center'}}>
              <div style={{position: 'relative'}}>
                <img src={photos[photoIndex]} alt={`${full_name} building`} style={{maxWidth: '100%', maxHeight: '200px', borderRadius: '6px', objectFit: 'cover'}} />
                {photos.length > 1 && (
                  <>
                    <Icon
                      name="chevron left"
                      link
                      style={{position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.8)', cursor: 'pointer'}}
                      onClick={(e) => { e.preventDefault(); setPhotoIndex((i) => (i - 1 + photos.length) % photos.length) }}
                    />
                    <Icon
                      name="chevron right"
                      link
                      style={{position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.8)', cursor: 'pointer'}}
                      onClick={(e) => { e.preventDefault(); setPhotoIndex((i) => (i + 1) % photos.length) }}
                    />
                    <div style={{marginTop: '6px', display: 'flex', justifyContent: 'center', gap: '6px'}}>
                      {photos.map((_, i) => (
                        <span
                          key={i}
                          role="button"
                          tabIndex={0}
                          onClick={() => setPhotoIndex(i)}
                          onKeyDown={(e) => e.key === 'Enter' && setPhotoIndex(i)}
                          style={{width: 8, height: 8, borderRadius: '50%', background: i === photoIndex ? '#666' : '#ccc', cursor: 'pointer'}}
                          aria-label={`Photo ${i + 1} of ${photos.length}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {(office_entrance_image_url || internal_directions) && (
            <Segment basic vertical style={{marginTop: '.5em', background: 'rgba(0,0,0,.03)', borderRadius: '6px'}}>
              <Card.Header as="strong"><Icon name="door open" /> Inside the building</Card.Header>
              {internal_directions && <Card.Description style={{marginTop: '.5em'}}>{internal_directions}</Card.Description>}
              {office_entrance_image_url && <div style={{marginTop: '.75em', textAlign: 'center'}}><img src={office_entrance_image_url} alt={`${full_name} office entrance`} style={{maxWidth: '100%', maxHeight: '180px', borderRadius: '6px', objectFit: 'cover'}} /></div>}
            </Segment>
          )}

          <Segment basic vertical>
            <ExpandableDescription label="Description" value={description} />
          </Segment>

          {covid_message && <Card.Description><Card.Header as="strong">COVID Message:</Card.Header> {covid_message}</Card.Description>}

          <Segment secondary style={detailsStyle}>
            { eligibility_requirements && <Card.Description><Card.Header as="strong">Eligibility:</Card.Header> {eligibility_requirements}</Card.Description>}
            {financial_information && <Card.Description><Card.Header as="strong">Cost:</Card.Header> {financial_information.includes(`\n`) ? financial_information.split('\n').map((paragraph, index) => index === 0 ? paragraph : <p key={index}>{paragraph}</p>) : financial_information}</Card.Description>}
            { intake_instructions && <Card.Description><Card.Header as="strong">Next Steps:</Card.Header> {intake_instructions.includes(`\n`) ? intake_instructions.split('\n').map((paragraph, index) => index === 0 ? paragraph : <p key={index}>{paragraph}</p>) : intake_instructions}</Card.Description>}
            {/* Languages is an array  */}
            { languages_offered && <Card.Description><Card.Header as="strong">Languages: </Card.Header>{languages_offered.join(", ")}</Card.Description> }
          </Segment>
          { (min_age && max_age) ? <Card.Description><Card.Header as="strong">Ages:</Card.Header> {min_age}-{max_age}</Card.Description>
            : (min_age && !max_age) ? <Card.Description><Card.Header as="strong">Minimum age served:</Card.Header> {min_age}</Card.Description>
            : (!min_age && max_age) ? <Card.Description><Card.Header as="strong">Maximum age served:</Card.Header> {max_age}</Card.Description>
            : null }
          {primaryCategory && (
            <Card.Description>
              <Card.Header as="strong">{primaryCategory.split(':')[0]}:</Card.Header>
              <NavLink to={`${basePath}/?category=${encodeURIComponent(primaryCategory)}`}> {primaryCategory.split(':')[1]}</NavLink>
              {listingCategories.slice(1).map((cat) => {
                const sub = cat.split(': ')[1]
                return sub ? (
                  <span key={cat}>
                    {', '}
                    <NavLink to={`${basePath}/?category=${encodeURIComponent(cat)}`}>{sub}</NavLink>
                  </span>
                ) : null
              })}
            </Card.Description>
          )}
        </Card.Content>
        {/* Show keywords and/or cost_keywords if they exist. If not, show category so cards have consistent design */}
        <Card.Content extra>
          { (keywords && cost_keywords) ? <KeywordDisplay arr={[...keywords, ...cost_keywords]} /> 
          : keywords ? <KeywordDisplay arr={keywords} /> : cost_keywords ? <KeywordDisplay arr={cost_keywords} />
          : primaryCategory ? <NavLink to={`${basePath}/?category=${encodeURIComponent(primaryCategory)}`}># {primaryCategory.split(':')[1]}</NavLink> : null}
        </Card.Content>
      </Card>
    </Ref>
  )
})

const ExpandableDescription = ({ label, value }) => <>
    { label && <Card.Header as="strong">{label}:</Card.Header> }
    { value && <Card.Description className="expandable" tabIndex="0">{value.split(`\n`).map((paragraph, index) => <p key={index}>{paragraph}</p>)}</Card.Description> }
</>

const MapMap = forwardRef(({ listings, cardRefs }, ref) => {
  return (
    <Ref innerRef={ref}>
      <Segment as={MapContainer} center={siteConfig.mapCenter} zoom={siteConfig.mapZoom} minZoom={6} maxZoom={18} scrollWheelZoom={false} tap={true} dragging={true} touchZoom={true}>
        <TileLayer attribution={siteConfig.siteName} url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
        <MapMarkers listings={listings} cardRefs={cardRefs} />
      </Segment>
    </Ref>
  )
})

function createDonutSvg(slices, count) {
  const size = Math.min(28 + Math.log2(count) * 8, 56)
  const strokeWidth = 5
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const center = size / 2
  const total = slices.reduce((sum, s) => sum + s.count, 0) || 1
  let offset = 0

  const arcs = slices.map((slice, i) => {
    const segLen = (slice.count / total) * circumference
    const dashOffset = -offset
    offset += segLen
    return `<circle cx="${center}" cy="${center}" r="${radius}" fill="none" stroke="${slice.color}" stroke-width="${strokeWidth}" stroke-dasharray="${segLen} ${circumference - segLen}" stroke-dashoffset="${dashOffset}" stroke-linecap="butt" transform="rotate(-90 ${center} ${center})"/>`
  }).join('')

  return `<svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
    <circle cx="${center}" cy="${center}" r="${radius}" fill="white" stroke="#ddd" stroke-width="0.5"/>
    ${arcs}
    <text x="${center}" y="${center}" text-anchor="middle" dominant-baseline="central" font-size="${size < 36 ? 11 : 13}" font-weight="bold" fill="#333">${count}</text>
  </svg>`
}

function createClusterIcon(cluster) {
  const markers = cluster.getAllChildMarkers()
  const count = cluster.getChildCount()
  const categoryCounts = {}
  markers.forEach(m => {
    const cat = m.options.category || 'Other'
    const parent = cat.split(': ')[0]
    categoryCounts[parent] = (categoryCounts[parent] || 0) + 1
  })
  const slices = Object.entries(categoryCounts).map(([cat, cnt]) => ({
    color: getCategoryHexColor(cat),
    count: cnt,
  }))
  const size = Math.min(28 + Math.log2(count) * 8, 56)
  const html = createDonutSvg(slices, count)
  return L.divIcon({
    html: `<div class="donut-cluster">${html}</div>`,
    className: 'donut-cluster-icon',
    iconSize: L.point(size + 4, size + 4),
    iconAnchor: L.point((size + 4) / 2, (size + 4) / 2),
  })
}

function createDotIcon(hexColor, isSelected) {
  const size = isSelected ? 16 : 10
  const border = isSelected ? `2px solid #333` : `2px solid white`
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${hexColor};border:${border};box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>`,
    className: 'category-dot-icon',
    iconSize: L.point(size, size),
    iconAnchor: L.point(size / 2, size / 2),
  })
}

function MapMarkers ({listings, cardRefs}) {
  const basePath = useBasePath()
  const { markerId } = useParams()
  const map = useMap()
  const mappedListings = useMemo(() => listings.filter(({ coords: [ lat, lon ] }) => lat && lon), [listings])
  const currentCoords = useMemo(() => listings.find(({ guid }) => guid === Number(markerId))?.coords, [listings, markerId])
  const bounds = useMemo(() => mappedListings.map(({coords}) => coords), [mappedListings])
  useEffect(() => {
    if (!bounds.length) return
    const el = document.getElementById('app')
    const scrollTop = el?.scrollTop || 0
    if (bounds.length === 1) {
      map.setView(bounds[0], 14)
    } else {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 })
    }
    requestAnimationFrame(() => { if (el) el.scrollTop = scrollTop })
  }, [map, bounds])
  useEffect(() => currentCoords && currentCoords[0] && currentCoords[1] && map.setView(currentCoords, 18), [map, currentCoords])

  return (<>
    <MarkerClusterGroup iconCreateFunction={createClusterIcon} maxClusterRadius={50} spiderfyOnMaxZoom showCoverageOnHover={false}>
      {mappedListings.map(listing => {
        const isSelected = markerId === `${listing.guid}`
        const hexColor = getCategoryHexColor(listing.category)
        const markerCategory = normalizeCategories(listing.category)[0] || listing.category
        return (
          <Marker key={listing.guid} position={listing.coords} icon={isSelected ? greenLMarker : createDotIcon(hexColor, false)} category={markerCategory} eventHandlers={{ click: ({latlng}) => map.setView(latlng) }}>
            <Tooltip>{listing.full_name}</Tooltip>
            <Popup>
              <Card style={{ border: `none`, boxShadow: `none` }}>
                <Card.Content>
                  <Card.Header><Link to={`${basePath}/${listing.guid}`}>{listing.full_name}</Link></Card.Header>
                  <Card.Meta>{listing.full_address}</Card.Meta>
                  <Segment basic vertical>
                    <Card.Content>
                      <div className="description">{listing.description}</div>
                    </Card.Content>
                  </Segment>
                  <Link to={`${basePath}/${listing.guid}`} onClick={() => { cardRefs[listing.guid].current?.scrollIntoView({behavior: "smooth"}) }} className="listing-show-details">Show Details</Link>
                  </Card.Content>
              </Card>
            </Popup>
          </Marker>
        )
      })}
    </MarkerClusterGroup>
  </>)
}

export default MapPage;
